import { useCallback, useEffect, useState } from 'react';
import type { Eu, Falha } from '../../acesso';
import type { Sessao } from '../../auth';
import {
  ROTULO_DA_CAUSA,
  lerPainelDasDivergencias,
  lerResponsaveis,
  salvarResponsaveis,
} from '../../auditoria';
import type { CausaDaDivergencia, PainelDasDivergencias, Responsaveis } from '../../auditoria';
import { ErroDeArea, SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { papeisDe, ultimaCentral } from './comum';
import './divergencias.css';

/**
 * INDICADORES DAS DIVERGÊNCIAS (dono, 13/10/2026). Mede pendência, tempo, qualidade e retrabalho —
 * nunca só velocidade, e sem ranking: contar rápido e errado não pode parecer bom. A tabela por
 * balconista é contexto para conversar, ordenada por volume de casos, não por "nota".
 */

const DIAS = [7, 30, 90] as const;
const minutos = (m: number | null) =>
  m === null ? '—' : m < 60 ? `${String(m)} min` : `${(m / 60).toFixed(1).replace('.', ',')} h`;

export function PainelDivergencias({ sessao, eu }: { sessao: Sessao; eu: Eu | null }) {
  const papeis = papeisDe(eu);
  const [dias, setDias] = useState<number>(30);
  const [p, setP] = useState<PainelDasDivergencias | null>(null);
  const [falha, setFalha] = useState<Falha | null>(null);

  const carregar = useCallback(async () => {
    const r = await lerPainelDasDivergencias(dias);
    if (r.ok) {
      setP(r.dados);
      setFalha(null);
    } else setFalha(r.falha);
  }, [dias]);
  useEffect(() => {
    if (papeis.gestao) void carregar();
  }, [carregar, papeis.gestao]);

  if (!papeis.gestao) return <SemAcesso area="Os indicadores das divergências" />;
  if (falha && !p)
    return (
      <section className="dp">
        <ErroDeArea oQue="os indicadores" aoTentar={() => void carregar()} />
      </section>
    );

  const maxDia = Math.max(1, ...(p?.concluidosPorDia.map((d) => d.total) ?? [1]));
  const corrigidosPct =
    p && p.qualidade.conferidos > 0
      ? Math.round((p.qualidade.corrigidos / p.qualidade.conferidos) * 100)
      : null;

  return (
    <section className="dp">
      <a className="dp-link" href={ultimaCentral()}>
        <Icone nome="arrow-l" tamanho={16} />
        Voltar às divergências
      </a>
      <header className="dp-topo">
        <div>
          <h1 className="dp-titulo">Indicadores das divergências</h1>
          <p className="dp-sub">Pendência, tempo e qualidade das conferências — não é ranking.</p>
        </div>
        <div className="dp-chips" role="group" aria-label="Período">
          {DIAS.map((d) => (
            <button
              key={d}
              type="button"
              className="dp-chip"
              aria-pressed={dias === d}
              onClick={() => setDias(d)}
            >
              {d} dias
            </button>
          ))}
        </div>
      </header>

      {!p ? (
        <p className="dp-dica" aria-busy="true">
          Carregando…
        </p>
      ) : (
        <>
          <div className="dp-kpis">
            {[
              { r: 'Prateleira pendente', v: p.pendentes.prateleira, i: 'shelf' as const },
              { r: 'Estoque pendente', v: p.pendentes.estoque, i: 'box' as const },
              { r: 'Aguardando decisão', v: p.pendentes.decisao, i: 'gauge' as const },
              { r: 'Não verificados', v: p.pendentes.naoVerificados, i: 'eye' as const },
              { r: 'Ajustes pendentes no HOS', v: p.pendentes.ajustesNoHos, i: 'edit' as const },
            ].map((k) => (
              <div key={k.r} className="dp-kpi" style={{ cursor: 'default' }}>
                <span className="dp-kpi-rotulo">
                  <Icone nome={k.i} tamanho={14} />
                  {k.r}
                </span>
                <span className="dp-kpi-valor">{k.v}</span>
              </div>
            ))}
          </div>

          <div className="dp-grade">
            <section className="dp-area">
              <h2 className="dp-area-titulo">Tempo até conferir (mediana)</h2>
              <dl className="dp-dados">
                <div>
                  <dt>Prateleira</dt>
                  <dd>{minutos(p.tempoAteConferir.prateleira)}</dd>
                </div>
                <div>
                  <dt>Estoque</dt>
                  <dd>{minutos(p.tempoAteConferir.estoque)}</dd>
                </div>
              </dl>
              <h2 className="dp-area-titulo">Qualidade e retrabalho</h2>
              <dl className="dp-dados">
                <div>
                  <dt>Casos conferidos</dt>
                  <dd>{p.qualidade.conferidos}</dd>
                </div>
                <div>
                  <dt>Contagens corrigidas</dt>
                  <dd>
                    {p.qualidade.corrigidos}
                    {corrigidosPct !== null ? ` (${String(corrigidosPct)}%)` : ''}
                  </dd>
                </div>
                <div>
                  <dt>Pediram nova conferência</dt>
                  <dd>{p.qualidade.novaConferencia}</dd>
                </div>
                <div>
                  <dt>Reabertos</dt>
                  <dd>{p.reabertos}</dd>
                </div>
                <div>
                  <dt>Sem dados suficientes</dt>
                  <dd>{p.inconclusivos}</dd>
                </div>
              </dl>
            </section>

            <section className="dp-area">
              <h2 className="dp-area-titulo">Concluídos por dia</h2>
              {p.concluidosPorDia.length === 0 ? (
                <p className="dp-dica">Nenhum caso concluído no período.</p>
              ) : (
                <div className="dp-barras" role="img" aria-label="Casos concluídos por dia">
                  {p.concluidosPorDia.slice(-30).map((d) => (
                    <div
                      key={d.dia}
                      title={`${d.dia.slice(8, 10)}/${d.dia.slice(5, 7)}: ${String(d.total)}`}
                    >
                      <small>{d.total}</small>
                      <span
                        style={{ height: `${String(Math.max(4, (d.total / maxDia) * 90))}%` }}
                      />
                      <small>{d.dia.slice(8, 10)}</small>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="dp-area">
              <h2 className="dp-area-titulo">Motivos (causa da resolução)</h2>
              {p.causas.length === 0 ? (
                <p className="dp-dica">Nenhuma resolução no período.</p>
              ) : (
                <table className="dp-tabela">
                  <tbody>
                    {p.causas.map((c) => (
                      <tr key={c.causa}>
                        <td data-col="mov">
                          {c.causa === 'SEM_CAUSA'
                            ? 'Sem causa (antes da nova resolução)'
                            : (ROTULO_DA_CAUSA[c.causa as CausaDaDivergencia] ?? c.causa)}
                        </td>
                        <td data-col="qtd" className="dp-num">
                          {c.total}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <section className="dp-area">
              <h2 className="dp-area-titulo">Produtos com mais divergências</h2>
              {p.produtosRecorrentes.length === 0 ? (
                <p className="dp-dica">Nenhum produto se repetiu no período.</p>
              ) : (
                <table className="dp-tabela">
                  <tbody>
                    {p.produtosRecorrentes.map((x) => (
                      <tr key={x.produtoId}>
                        <td data-col="mov">{x.produto}</td>
                        <td data-col="qtd" className="dp-num">
                          {x.casos} casos
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>

          <section className="dp-area">
            <h2 className="dp-area-titulo">Divergências por balconista</h2>
            <p className="dp-dica">
              Volume de casos, não nota: um caso aberto é uma pergunta. "Erro" só conta o que foi
              confirmado com prova.
            </p>
            {p.porBalconista.length === 0 ? (
              <p className="dp-dica">Nenhum caso no período.</p>
            ) : (
              <table className="dp-tabela">
                <thead>
                  <tr>
                    <th scope="col">Balconista</th>
                    <th scope="col" className="dp-num">
                      Casos
                    </th>
                    <th scope="col" className="dp-num">
                      Concluídos
                    </th>
                    <th scope="col" className="dp-num">
                      Erros confirmados
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {p.porBalconista.map((b) => (
                    <tr key={b.accountId}>
                      <td data-col="mov">{b.nome}</td>
                      <td data-col="qtd" className="dp-num">
                        {b.casos}
                      </td>
                      <td data-col="doc" className="dp-num">
                        {b.concluidos}
                      </td>
                      <td data-col="hora" className="dp-num">
                        {b.erros}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}

      {papeis.administra && <Escala sessao={sessao} />}
    </section>
  );
}

/** Quem confere o quê (arrumação da "Minha fila"; a permissão continua sendo a capacidade). */
function Escala({ sessao }: { sessao: Sessao }) {
  const [r, setR] = useState<Responsaveis | null>(null);
  const [prat, setPrat] = useState<string[]>([]);
  const [est, setEst] = useState<string[]>([]);
  const [motivo, setMotivo] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null);
  const [enviando, setEnviando] = useState(false);
  const carregar = useCallback(async () => {
    const x = await lerResponsaveis();
    if (x.ok) {
      setR(x.dados);
      setPrat(x.dados.prateleira.map((y) => y.id));
      setEst(x.dados.estoque.map((y) => y.id));
    }
  }, []);
  useEffect(() => {
    void carregar();
  }, [carregar]);
  if (!r) return null;
  const alternar = (lista: string[], id: string) =>
    lista.includes(id) ? lista.filter((x) => x !== id) : [...lista, id];
  return (
    <section className="dp-area">
      <h2 className="dp-area-titulo">Quem confere o quê</h2>
      <p className="dp-dica">
        Define a "Minha fila" de cada pessoa. Só aparece quem tem a permissão do local (em Perfis).
      </p>
      <div className="dp-dois">
        {(
          [
            ['Prateleira (área de vendas)', 'prateleira', prat, setPrat],
            ['Estoque', 'estoque', est, setEst],
          ] as const
        ).map(([titulo, campo, lista, mudar]) => (
          <fieldset key={campo} className="dp-local" style={{ margin: 0 }}>
            <legend className="dp-local-cab" style={{ padding: 0 }}>
              {titulo}
            </legend>
            {r.candidatos
              .filter((c) => c[campo])
              .map((c) => (
                <label key={c.id} className="dp-check">
                  <input
                    type="checkbox"
                    checked={lista.includes(c.id)}
                    onChange={() => mudar(alternar([...lista], c.id))}
                  />
                  {c.nome}
                </label>
              ))}
          </fieldset>
        ))}
      </div>
      <label className="dp-rotulo">
        Motivo da mudança
        <input
          className="dp-campo"
          value={motivo}
          maxLength={200}
          onChange={(e) => setMotivo(e.target.value)}
        />
      </label>
      {msg && (
        <div className={`dp-alerta ${msg.ok ? 'dp-alerta-info' : 'dp-alerta-erro'}`} role="status">
          {msg.texto}
        </div>
      )}
      <div className="dp-form-botoes">
        <button
          type="button"
          className="dp-btn dp-btn-escuro"
          disabled={motivo.trim().length < 5 || enviando}
          onClick={() => {
            setEnviando(true);
            void salvarResponsaveis(sessao, {
              prateleira: prat,
              estoque: est,
              motivo: motivo.trim(),
            }).then(async (x) => {
              setEnviando(false);
              if (x.tipo === 'confirmada') {
                setMsg({ ok: true, texto: 'Escala salva.' });
                setMotivo('');
                await carregar();
              } else
                setMsg({
                  ok: false,
                  texto:
                    x.tipo === 'incerta'
                      ? 'Não deu para confirmar (conexão). Tente de novo.'
                      : x.mensagem,
                });
            });
          }}
        >
          Salvar escala
        </button>
      </div>
    </section>
  );
}
